import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  BackgroundVariant,
  Node,
  Edge,
  MarkerType,
} from 'reactflow'
import 'reactflow/dist/style.css'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import {
  ArrowLeftIcon,
  SparklesIcon,
  CodeBracketIcon,
  DocumentTextIcon,
  ChartBarIcon,
  TableCellsIcon,
  PresentationChartBarIcon,
  BeakerIcon,
  BookOpenIcon,
  BoltIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  FlagIcon,
  CpuChipIcon,
  PlayIcon,
} from '@heroicons/react/24/outline'
import apiClient from '@/services/api'

// Icon mapping for node types
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  sparkles: SparklesIcon,
  code: CodeBracketIcon,
  document: DocumentTextIcon,
  chart: ChartBarIcon,
  table: TableCellsIcon,
  presentation: PresentationChartBarIcon,
  beaker: BeakerIcon,
  book: BookOpenIcon,
  bolt: BoltIcon,
  'magnifying-glass': MagnifyingGlassIcon,
  'arrow-path': ArrowPathIcon,
  flag: FlagIcon,
  cpu: CpuChipIcon,
}

// Custom node component for Ziro graph
const ZiroNode = ({ data }: { data: any }) => {
  const Icon = iconMap[data.icon] || CpuChipIcon

  const typeColors: Record<string, { bg: string; border: string; text: string }> = {
    supervisor: { bg: 'bg-purple-500/15', border: 'border-purple-500', text: 'text-purple-600' },
    specialist: { bg: 'bg-blue-500/15', border: 'border-blue-500', text: 'text-blue-600' },
    review: { bg: 'bg-yellow-500/15', border: 'border-yellow-500', text: 'text-yellow-600' },
    coordinator: { bg: 'bg-green-500/15', border: 'border-green-500', text: 'text-green-600' },
    executor: { bg: 'bg-green-500/15', border: 'border-green-500', text: 'text-green-600' },
    aggregator: { bg: 'bg-red-500/15', border: 'border-red-500', text: 'text-red-600' },
  }

  const colors = typeColors[data.type] || { bg: 'bg-muted', border: 'border-border', text: 'text-foreground' }

  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg border-2 ${colors.bg} ${colors.border} min-w-[180px]`}>
      <div className="flex items-center gap-2">
        <div className={`p-1.5 rounded-lg ${colors.bg}`}>
          <Icon className={`w-5 h-5 ${colors.text}`} />
        </div>
        <div>
          <div className={`font-semibold ${colors.text} text-sm`}>{data.label}</div>
          <div className="text-xs text-muted-foreground">{data.type}</div>
        </div>
      </div>
      {data.description && (
        <div className="text-xs text-muted-foreground mt-2 line-clamp-2">{data.description}</div>
      )}
    </div>
  )
}

const nodeTypes = {
  ziro_supervisor: ZiroNode,
  ziro_specialist: ZiroNode,
  ziro_review: ZiroNode,
  ziro_coordinator: ZiroNode,
  ziro_executor: ZiroNode,
  ziro_aggregator: ZiroNode,
}

interface GraphData {
  id: string
  name: string
  description: string
  is_system: boolean
  nodes: Array<{
    id: string
    type: string
    position: { x: number; y: number }
    data: Record<string, any> & {
      label?: string
      description?: string
      icon?: string
      specialist_type?: string
    }
  }>
  edges: Array<{
    id: string
    source: string
    target: string
    label?: string
    animated?: boolean
    data: Record<string, any> & {
      condition?: string | null
      is_conditional?: boolean
    }
  }>
  metadata?: Record<string, any> & {
    name?: string
    description?: string
    version?: string
    features?: string[]
  }
}

const ZiroSystemWorkflowPage = () => {
  const navigate = useNavigate()
  const [graphData, setGraphData] = useState<GraphData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchGraph = async () => {
      try {
        setIsLoading(true)
        const data = await apiClient.getZiroGraphAsWorkflow()
        setGraphData(data)
      } catch (err) {
        setError('Failed to load Ziro Intelligence graph')
        console.error(err)
      } finally {
        setIsLoading(false)
      }
    }
    fetchGraph()
  }, [])

  // Convert API data to ReactFlow format
  const { nodes, edges } = useMemo(() => {
    if (!graphData) return { nodes: [], edges: [] }

    const flowNodes: Node[] = graphData.nodes.map((node) => ({
      id: node.id,
      type: node.type,
      position: node.position,
      data: {
        label: node.data.label,
        description: node.data.description,
        icon: node.data.icon,
        type: node.type.replace('ziro_', ''),
      },
      draggable: false,
    }))

    const flowEdges: Edge[] = graphData.edges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      label: edge.label || undefined,
      animated: edge.animated,
      type: 'smoothstep',
      style: edge.data.is_conditional
        ? { stroke: '#f59e0b', strokeDasharray: '5,5' }
        : { stroke: '#6b7280' },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: edge.data.is_conditional ? '#f59e0b' : '#6b7280',
      },
      labelStyle: { fontSize: 10, fill: '#6b7280' },
    }))

    return { nodes: flowNodes, edges: flowEdges }
  }, [graphData])

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-muted-foreground mt-4">Loading Ziro Intelligence graph...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <Card className="text-center p-8">
          <p className="text-red-600 mb-4">{error}</p>
          <Button variant="secondary" onClick={() => navigate('/workflows')}>
            Back to Workflows
          </Button>
        </Card>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-card border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/workflows')}>
              <ArrowLeftIcon className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <SparklesIcon className="w-6 h-6 text-purple-600" />
                <h1 className="text-xl font-bold text-foreground">
                  {graphData?.name || 'Ziro Intelligence System'}
                </h1>
                <Badge variant="primary">System Workflow</Badge>
                <Badge variant="success">Read-Only</Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {graphData?.description || 'Pre-built intelligent multi-agent execution workflow'}
              </p>
            </div>
          </div>
          <Button
            variant="primary"
            onClick={() => navigate('/intelligent')}
          >
            <PlayIcon className="w-4 h-4 mr-1" />
            Execute with This System
          </Button>
        </div>
      </div>

      {/* Info Bar */}
      <div className="bg-purple-500/10 border-b border-purple-500/20 px-4 py-3">
        <div className="flex items-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-purple-600 font-medium">Nodes:</span>
            <span className="text-purple-600">{nodes.length}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-purple-600 font-medium">Edges:</span>
            <span className="text-purple-600">{edges.length}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-purple-600 font-medium">Version:</span>
            <span className="text-purple-600">{graphData?.metadata?.version || '1.0'}</span>
          </div>
        </div>
      </div>

      {/* Features Panel */}
      {graphData?.metadata?.features && (
        <div className="bg-card border-b border-border px-4 py-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-foreground">Features:</span>
            {graphData.metadata.features.map((feature, idx) => (
              <Badge key={idx} variant="gray" className="text-xs">
                {feature}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Graph Canvas */}
      <div className="flex-1">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          attributionPosition="bottom-left"
          className="bg-muted/50"
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          panOnDrag
          zoomOnScroll
        >
          <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="#e5e7eb" />
          <Controls showInteractive={false} />
          <MiniMap
            nodeColor={(node) => {
              const colors: Record<string, string> = {
                ziro_supervisor: '#a855f7',
                ziro_specialist: '#3b82f6',
                ziro_review: '#eab308',
                ziro_coordinator: '#22c55e',
                ziro_executor: '#22c55e',
                ziro_aggregator: '#ef4444',
              }
              return colors[node.type || ''] || '#6b7280'
            }}
            maskColor="rgba(0, 0, 0, 0.1)"
          />
        </ReactFlow>
      </div>

      {/* Legend */}
      <div className="bg-card border-t border-border px-4 py-3">
        <div className="flex items-center gap-6 text-xs">
          <span className="font-medium text-foreground">Legend:</span>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-purple-500"></div>
            <span>Supervisor</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-blue-500"></div>
            <span>Specialists</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-green-500"></div>
            <span>Coordinators</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-yellow-500"></div>
            <span>Reviews</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-red-500"></div>
            <span>Aggregator</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 border-t-2 border-dashed border-orange-400"></div>
            <span>Conditional</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ZiroSystemWorkflowPage
